import { SidebarContent } from '@/components/blocks/SideBar/parts/Content';
import { sidebarSX } from '@/components/blocks/SideBar/styles/appBar/container';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useMobileApp } from '@/utils/useMobileApp';
import { AppBar, Breakpoint, Button, Skeleton, Stack, SwipeableDrawer } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { FC, PropsWithChildren, useCallback, useState } from 'react';
import { useResponsive } from 'shared/custom/hooks/MediaQuery';

type Props = {
	title?: string;
	href?: string;
	mobileBreakpoint: Breakpoint | number;
	scrollable?: boolean;
	elevation?: number;
	sidebarPaperSx?: SxProps;
	messages?: {
		action: string;
		drawer: string;
	};
};

export const Sidebar: FC<PropsWithChildren<Props>> = ({
	title = '',
	messages,
	href,
	mobileBreakpoint = 'sm',
	scrollable = false,
	children,
	elevation = 1,
	sidebarPaperSx = {},
}) => {
	const theme = useTheme();
	const { isMobileApp } = useMobileApp();
	const [open, setOpen] = useState<boolean>(false);
	const mobileQuery =
		typeof mobileBreakpoint === 'number' ? mobileBreakpoint - 1 : mobileBreakpoint;
	const { isMobile, isLoading } = useResponsive(mobileQuery);
	const openPanel = () => setOpen(true);
	const closePanel = () => setOpen(false);
	const togglePanel = useCallback(() => {
		if (isMobile) setOpen(!open);
	}, [open, isMobile]);

	if (isLoading)
		return (
			<Stack sx={sidebarSX.skeletonContainer}>
				<Skeleton height="22px" variant="rounded" />
			</Stack>
		);

	if (isMobile)
		return (
			<>
				<AppBar
					component="div"
					position="fixed"
					color="inherit"
					sx={[sidebarSX.appBar, isMobileApp ? { bottom: 0 } : {}] as SxProps<Theme>}
					onClick={togglePanel}
					elevation={2}
				>
					<Stack direction="row" spacing={0.5} sx={sidebarSX.appBarStack}>
						<Button
							variant="contained"
							color="primary"
							onClick={togglePanel}
							fullWidth
							sx={sidebarSX.appBarBtnAction}
						>
							<DynamicIcon name="filterList" />
							{messages?.action || title}
						</Button>
					</Stack>
				</AppBar>
				<SwipeableDrawer
					anchor="left"
					open={open}
					onOpen={openPanel}
					onClose={closePanel}
					PaperProps={{
						sx: sidebarSX.mobileDrawerPaper,
					}}
				>
					<SidebarContent
						{...{
							children,
							href,
							isMobile,
							scrollable,
							toggleOpen: togglePanel,
							elevation,
							sidebarPaperSx,
							title: messages?.drawer || title,
						}}
					/>
				</SwipeableDrawer>
			</>
		);

	return (
		<SidebarContent
			{...{
				children,
				href,
				isMobile,
				scrollable,
				toggleOpen: togglePanel,
				elevation,
				sidebarPaperSx,
				title: messages?.drawer || title,
			}}
		/>
	);
};
